import CustomDropdown2 from 'general/components/Others/CustomDropdown2';
import AppColors from 'general/constants/AppColors';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

const fetchChargingPartnerSubItems = async (item) => {
  const page = item?.page || 0;
  const q = item?.q || '';

  try {
    if (item?.role === 'VEHICLE_IDENTITY_PARTNER') {
      const { default: ChargeHelper } = await import('features/Charge/chargeHelper');
      const data = await ChargeHelper.API.requestGetChargeIdentityPartnerList(
        Utils.filterParams({ page, limit: 10, q })
      );
      return {
        values: (data?.partners ?? []).map((partner) => ({
          id: partner?.vehicleIdentityPartnerId,
          name: partner?.name,
          partnerType: 'VEHICLE_IDENTITY_PARTNER',
          item: partner,
        })),
        total: data?.total ?? 0,
      };
    }

    if (item?.role === 'VOUCHER_PARTNER') {
      const { default: AgencyHelper } = await import('features/Agency/agencyHelper');
      const today = new Date().toISOString().slice(0, 10);
      const data = await AgencyHelper.API.requestGetAgencyList(
        Utils.filterParams({
          page,
          limit: 10,
          q,
          from: '2024-01-01',
          to: today,
          role: 'VOUCHER_PARTNER',
          status: 'NORMAL',
        })
      );
      return {
        values: (data?.agencies ?? []).map((agency) => ({
          id: agency?.agencyId,
          name: agency?.name,
          partnerType: 'VOUCHER_PARTNER',
          item: agency,
        })),
        total: data?.total ?? 0,
      };
    }
  } catch {
    // Dropdown rỗng khi API lỗi.
  }

  return { values: [], total: 0 };
};

ChargingPartnerDropdown.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  selectedValue: PropTypes.object,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};

const PARTNER_TYPE_DEFAULT_VALUE = {
  VEHICLE_IDENTITY_PARTNER: {
    name: 'Đối tác sạc',
    role: 'VEHICLE_IDENTITY_PARTNER',
  },
  VOUCHER_PARTNER: {
    name: 'Đối tác mua voucher',
    role: 'VOUCHER_PARTNER',
  },
};

function ChargingPartnerDropdown({
  id,
  name,
  selectedValue,
  onChange,
  placeholder = 'Chọn đối tác',
  disabled = false,
}) {
  const defaultValue = useMemo(
    () => PARTNER_TYPE_DEFAULT_VALUE[selectedValue?.partnerType] ?? {},
    [selectedValue?.partnerType]
  );
  const defaultItem = useMemo(() => {
    if (!selectedValue?.partnerType || !Utils.isValid(selectedValue?.id)) return null;
    return {
      role: selectedValue.partnerType,
      item: selectedValue,
    };
  }, [selectedValue]);

  const handleChange = (value) => onChange?.(value || null);

  return (
    <CustomDropdown2
      key={`charging_partner_${selectedValue?.partnerType ?? 'none'}_${selectedValue?.id ?? 'empty'}`}
      id={id}
      name={name}
      ignoreConfirmClick={true}
      disabled={disabled}
      toggleElement={
        <button
          className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between ${
            disabled ? 'disabled' : ''
          }`}
          style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          type="button"
        >
          {selectedValue?.name ?? placeholder}
        </button>
      }
      selectedValue={selectedValue}
      dropdownMenuItems={[
        { name: 'Đối tác sạc', role: 'VEHICLE_IDENTITY_PARTNER' },
        { name: 'Đối tác mua voucher', role: 'VOUCHER_PARTNER' },
      ]}
      getSubItems={fetchChargingPartnerSubItems}
      defaultValue={defaultValue}
      defaultItem={defaultItem}
      allowBackFromDefault={true}
      dropdownMenuStyle={{ maxHeight: '30vh' }}
      dropdownMenuClassName="overflow-auto w-100"
      onChange={handleChange}
      isDropdownForm={true}
      titleSearchable={false}
      searchable={true}
      searchFormClassName="mx-3 my-1 search-form-border"
      footerEl={
        <div
          className="p-4 position-sticky sticky-bottom bg-white"
          style={{ borderTop: `1px solid ${AppColors.gray[300]}` }}
        >
          {UIHelper.buildNegativeButton('Bỏ chọn', () => handleChange(null))}
        </div>
      }
    />
  );
}

export default ChargingPartnerDropdown;
